import React, { useEffect, useState } from "react";
import ariaLogo from "../../../svg/aria-university-logo.svg";

/** Keep Western/English digits in print PDFs (also normalize Persian/Arabic-Indic → 0-9). */
export const faDigits = (value) =>
  String(value ?? "").replace(/[۰-۹٠-٩]/g, (digit) => {
    const persian = "۰۱۲۳۴۵۶۷۸۹";
    const arabicIndic = "٠١٢٣٤٥٦٧٨٩";
    const p = persian.indexOf(digit);
    if (p >= 0) return String(p);
    const a = arabicIndic.indexOf(digit);
    return a >= 0 ? String(a) : digit;
  });

/** Build a Safe PDF/Save-as filename-friendly document title. */
export function buildPrintDocumentTitle(parts = []) {
  const cleaned = (Array.isArray(parts) ? parts : [parts])
    .map((part) =>
      String(part ?? "")
        .trim()
        .replace(/[/\\:*?"<>|]+/g, " ")
        .replace(/\s+/g, " ")
        .trim()
    )
    .filter(Boolean)
    .join(" - ")
    .replace(/(?: - )+/g, " - ")
    .trim()
    .slice(0, 120);
  return cleaned || "گزارش آریا";
}

/** Temporarily set document.title for Print → Save as PDF filenames. */
export function usePrintDocumentTitle(title) {
  useEffect(() => {
    if (!title) return undefined;
    const previous = document.title;
    document.title = title;
    return () => {
      document.title = previous;
    };
  }, [title]);
}

/** Flash a temporary print/popup warning; never leave it stuck. */
export function flashPrintBlocked(setMessage, text = "اجازه باز کردن پنجره چاپ را فعال کنید.", ms = 5000) {
  if (typeof setMessage !== "function") return;
  setMessage(text);
  window.setTimeout(() => {
    setMessage((current) => (current === text ? "" : current));
  }, ms);
}

export const printTime = (time) => {
  if (time == null || time === "") return "—";
  const raw = String(time);
  const match = raw.match(/(\d{1,2}):(\d{2})/);
  if (!match) return faDigits(raw);
  const hour = Number(match[1]);
  const minute = match[2];
  const hour12 = ((hour + 11) % 12) + 1;
  const suffix = hour >= 12 ? "PM" : "AM";
  return `\u202A${faDigits(String(hour12).padStart(2, "0"))}:${faDigits(minute)} ${suffix}\u202C`;
};

export const shortShamsi = (value) => {
  const match = String(value || "").match(/^(\d{4})-(\d{1,2})-(\d{1,2})$/);
  return match
    ? `${String(match[2]).padStart(2, "0")}/${String(match[3]).padStart(2, "0")}`
    : String(value || "");
};

/** Gregorian YYYY-MM-DD → Jalali YYYY/MM/DD (Intl persian calendar). */
export const toShamsiDate = (isoDate) => {
  const match = String(isoDate || "").match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!match) return "";
  const date = new Date(Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3]), 12));
  try {
    const parts = new Intl.DateTimeFormat("en-u-ca-persian", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    }).formatToParts(date);
    const get = (type) => parts.find((part) => part.type === type)?.value;
    const year = get("year");
    const month = get("month");
    const day = get("day");
    if (!year || !month || !day) return "";
    return `${year}/${month}/${day}`;
  } catch (_e) {
    return "";
  }
};

export const weekdayFa = (name) => {
  const map = {
    "یک شنبه": "یک‌شنبه",
    یکشنبه: "یک‌شنبه",
    "دو شنبه": "دوشنبه",
    دوشنبه: "دوشنبه",
    "سه شنبه": "سه‌شنبه",
    "سه‌شنبه": "سه‌شنبه",
    "چهار شنبه": "چهارشنبه",
    چهارشنبه: "چهارشنبه",
    "پنج شنبه": "پنج‌شنبه",
    پنجشنبه: "پنج‌شنبه",
  };
  const trimmed = String(name || "").trim();
  return map[trimmed] || trimmed;
};

export const attendanceMark = (state) => {
  if (Number(state) === 1) return "حاضر";
  if (Number(state) === 2) return "غایب";
  return "—";
};

export const chunkDays = (days, max = 32) => {
  const list = Array.isArray(days) ? days : [];
  if (!list.length) return [[]];
  if (list.length <= max) return [list];
  const sheetCount = Math.ceil(list.length / max);
  const size = Math.ceil(list.length / sheetCount);
  const sheets = [];
  for (let i = 0; i < list.length; i += size) sheets.push(list.slice(i, i + size));
  return sheets;
};

export const sheetDensity = (dayCount, type) => {
  const load = Number(dayCount || 0) + 6;
  let density = load <= 22 ? "normal" : load <= 30 ? "compact" : "dense";
  if (String(type) === "0" && density === "normal") density = "compact";
  return density;
};

/**
 * Open a routed print page from a user gesture.
 * Avoid windowFeatures like "noopener" — some Chromium builds treat that as a failed open.
 */
export function openPrintRoute(path) {
  const target = window.open(path, "_blank");
  if (!target) return null;
  try {
    target.opener = null;
  } catch (_) {
    /* ignore */
  }
  return target;
}

export function usePrintData(loader, deps) {
  const [state, setState] = useState({ loading: true, error: "", data: null, summary: null });
  useEffect(() => {
    let active = true;
    setState({ loading: true, error: "", data: null, summary: null });
    Promise.resolve()
      .then(loader)
      .then((result) => {
        if (!active) return;
        setState({
          loading: false,
          error: "",
          data: result?.data ?? result,
          summary: result?.summary || null,
        });
      })
      .catch((err) => {
        if (!active) return;
        setState({
          loading: false,
          error: err?.response?.data?.error?.message || err.message || "گزارش آماده نشد.",
          data: null,
          summary: null,
        });
      });
    return () => {
      active = false;
    };
  }, deps);
  return state;
}

export function ConfirmMark({ value }) {
  if (value == null || value === "") return <span className="aria-confirm is-empty">—</span>;
  return Number(value) === 1 ? (
    <span className="aria-confirm is-yes">✓</span>
  ) : (
    <span className="aria-confirm is-no">×</span>
  );
}

export function ReportSignatures({ labels = [] }) {
  return (
    <div className="aria-report-signatures">
      {labels.map((label) => (
        <p key={label}>
          <span />
          {label}
        </p>
      ))}
    </div>
  );
}

export function ReportHeader({ rightLines = [], leftLines = [] }) {
  const officialLines = leftLines.length
    ? leftLines
    : ["وزارت تحصیلات عالی", "پوهنتون آریا", "معاونیت علمی"];

  return (
    <header className="aria-report-head print_head">
      <div className="aria-report-head-inner print_main_head">
        <div className="aria-report-right print_right two_head">
          {rightLines.filter(Boolean).map((line, index) => (
            <p key={`r-${index}`}>{line}</p>
          ))}
        </div>
        <div className="aria-report-center print_center">
          <i className="aria-report-logo print_head_logo">
            <img src={ariaLogo} alt="پوهنتون آریا" />
          </i>
        </div>
        <div className="aria-report-left print_left">
          {officialLines.filter(Boolean).map((line, index) => (
            <p key={`l-${index}`}>{line}</p>
          ))}
        </div>
      </div>
    </header>
  );
}

export function ReportMeta({ items = [] }) {
  const rows = (items || []).filter((item) => item && item.value != null && String(item.value).trim() !== "");
  if (!rows.length) return null;
  return (
    <dl className="print-report-meta">
      {rows.map((item) => (
        <div className="print-report-meta-item" key={item.label}>
          <dt>{item.label}</dt>
          <dd>{item.value}</dd>
        </div>
      ))}
    </dl>
  );
}

export default function AriaReport({
  title,
  subtitle,
  documentTitle,
  metaItems = [],
  rightLines = [],
  leftLines = [],
  landscape = false,
  pageMargin,
  showHeader = true,
  className = "",
  children,
}) {
  const pageSize = landscape ? "A4 landscape" : "A4 portrait";
  const margin = pageMargin || (landscape ? "5mm" : "10mm");
  const pdfTitle = documentTitle || buildPrintDocumentTitle([title, subtitle]);
  usePrintDocumentTitle(pdfTitle);
  return (
    <main className={`aria-report ${landscape ? "is-landscape" : "is-portrait"} ${className}`} dir="rtl">
      <style>{`@page{size:${pageSize};margin:${margin}}`}</style>
      <div className="aria-report-actions no-print">
        <button type="button" onClick={() => window.print()}>
          چاپ / Print
        </button>
      </div>
      {showHeader ? <ReportHeader rightLines={rightLines} leftLines={leftLines} /> : null}
      {(title || subtitle) && (
        <div className="aria-report-title">
          {title ? <h1>{title}</h1> : null}
          {subtitle ? <p>{subtitle}</p> : null}
        </div>
      )}
      <ReportMeta items={metaItems} />
      {children}
    </main>
  );
}
